import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Users,
  Search,
  Filter,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  Check,
  X,
  UserPlus,
  UserMinus,
  AlertCircle
} from 'lucide-react';
import {
  Button,
  Input,
  Badge,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui';
import { parseLocalDate } from '../../lib/dateUtils';
import { YearMultiSelect } from '../ui/year-multi-select';
import {
  getAllPlayersForAssignment,
  getGroupPlayers,
  assignPlayersToGroup,
  removePlayersFromGroup,
  inscribirJugadores
} from '../../lib/supabase';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { DEFAULT_SPORT, normalizeSportValue } from '../../constants/sports';
import { isActiveGroupRelation, isVisiblePlayer } from '../../lib/playerVisibility';
import { normalizarTipoRestriccion } from '../../lib/categorias';

const getPlayerGroupNames = (player) => {
  if (!player?.jugadores_grupos || player.jugadores_grupos.length === 0) {
    return 'Sin grupo';
  }
  const activeGroups = player.jugadores_grupos.filter((jg) => isActiveGroupRelation(jg));
  if (activeGroups.length === 0) {
    return 'Sin grupo';
  }
  return activeGroups
    .map((jg) => jg.grupos?.nombre_del_grupo || 'Grupo desconocido')
    .join(', ');
};

const STATUS_ALIASES = {
  activo: ['activo', 'activa'],
  inactivo: ['inactivo', 'inactiva'],
  suspendido: ['suspendido', 'suspension']
};

const normalizeStatusValue = (value) => {
  if (!value) return '';
  return value.toString().trim().toLowerCase();
};

const normalizeRamaValue = (value) => {
  if (!value) return '';
  return value.toString().trim().toLowerCase();
};

const isRamaAllowed = (groupRamaRaw, playerRamaRaw) => {
  const groupRama = normalizeRamaValue(groupRamaRaw);
  if (!groupRama) return true;
  if (groupRama === 'mixto') {
    return ['varonil', 'femenil'].includes(normalizeRamaValue(playerRamaRaw));
  }
  return normalizeRamaValue(playerRamaRaw) === groupRama;
};
const GroupRosterModal = ({ group, isOpen, initialTab = 'assign', onClose, onSuccess }) => {
  const { confirm, alert, ConfirmDialog } = useConfirmDialog();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [ramaFilter, setRamaFilter] = useState('todos');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [playerTipoFilter, setPlayerTipoFilter] = useState('todos');
  const [selectedYearsFilter, setSelectedYearsFilter] = useState([]);
  const [showOnlyEligible, setShowOnlyEligible] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState(new Set());
  const [allPlayers, setAllPlayers] = useState([]);
  const [groupPlayers, setGroupPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [inlineError, setInlineError] = useState(null);

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const groupRestrictionType = useMemo(
    () => normalizarTipoRestriccion(group?.tipo_restriccion),
    [group]
  );

  // La categoria marca el camino; el GRUPO decide si obliga. Con 'sin_restriccion'
  // no hay nada que marcar: ni filtro, ni aviso, ni bloqueo.
  const categoriaRestringe = groupRestrictionType !== 'sin_restriccion';

  const buildBoundaryDate = (year, month, day, boundType) => {
    if (typeof year !== 'number') return null;
    if (typeof month === 'number' && typeof day === 'number') {
      return new Date(year, month - 1, day);
    }
    return boundType === 'sup' ? new Date(year, 0, 1) : new Date(year, 11, 31);
  };

  const groupCategoryRules = useMemo(() => {
    const categorias = Array.isArray(group?.categorias)
      ? group.categorias.filter((categoria) => categoria?.activo !== false)
      : [];
    return categorias
      .map((categoria) => {
        const deporte = normalizeSportValue(categoria?.deporte || group?.deporte || DEFAULT_SPORT);
        const id = categoria.id || categoria.nombre;
        const nombre = categoria.nombre || 'Categoria';

        if (deporte === 'tackle') {
          const supAge = typeof categoria.tackle_edad_sup === 'number' ? categoria.tackle_edad_sup : null;
          const infAge = typeof categoria.tackle_edad_inf === 'number' ? categoria.tackle_edad_inf : null;
          const supYear = supAge !== null ? currentYear - supAge : null;
          const infYear = infAge !== null ? currentYear - infAge : null;

          const supDate = buildBoundaryDate(
            supYear,
            categoria.tackle_limite_sup_mes,
            categoria.tackle_limite_sup_dia,
            'sup'
          );
          const infDate = buildBoundaryDate(
            infYear,
            categoria.tackle_limite_inf_mes,
            categoria.tackle_limite_inf_dia,
            'inf'
          );
          const hasAnyLimit = Boolean(supDate || infDate || supYear !== null || infYear !== null);

          return {
            id,
            nombre,
            deporte,
            supDate,
            infDate,
            hasLimits: hasAnyLimit
          };
        }

        const anioInicio = typeof categoria.anio_inicio === 'number' ? categoria.anio_inicio : null;
        const anioFin = typeof categoria.anio_fin === 'number' ? categoria.anio_fin : null;
        const edadMin = typeof categoria.edad_min === 'number' ? categoria.edad_min : null;
        const edadMax = typeof categoria.edad_max === 'number' ? categoria.edad_max : null;
        const resolvedAnioInicio =
          anioInicio === null && edadMin !== null ? currentYear - edadMin : anioInicio;
        const resolvedAnioFin =
          anioFin === null && edadMax !== null ? currentYear - edadMax : anioFin;
        const hasAnyLimit =
          resolvedAnioInicio !== null ||
          resolvedAnioFin !== null ||
          edadMin !== null ||
          edadMax !== null;
        return {
          id,
          nombre,
          deporte,
          anio_inicio: resolvedAnioInicio,
          anio_fin: resolvedAnioFin,
          hasLimits: hasAnyLimit
        };
      })
      .filter((rule) => rule.hasLimits);
  }, [group, currentYear]);

  const formatCategoryRange = (rule) => {
    if (rule.deporte === 'tackle') {
      const supDate = rule.supDate;
      const infDate = rule.infDate;
      const formatDate = (date) => {
        if (!date) return null;
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };
      const supLabel = formatDate(supDate);
      const infLabel = formatDate(infDate);
      if (supLabel && infLabel) return `Nacidos del ${supLabel} al ${infLabel}`;
      if (supLabel) return `Nacidos desde ${supLabel}`;
      if (infLabel) return `Nacidos hasta ${infLabel}`;
      return 'Sin limites definidos';
    }

    const hasStart = typeof rule.anio_inicio === 'number';
    const hasEnd = typeof rule.anio_fin === 'number';
    if (hasStart && hasEnd) {
      const yearMax = rule.anio_inicio;
      const yearMin = rule.anio_fin;
      if (yearMin === yearMax) return `Nacidos en ${yearMin}`;
      return `Nacidos ${yearMin}-${yearMax}`;
    }
    if (hasStart) return `Nacidos hasta ${rule.anio_inicio}`;
    if (hasEnd) return `Nacidos desde ${rule.anio_fin}`;
    return 'Sin limites definidos';
  };

  useEffect(() => {
    if (isOpen && group?.id) {
      setActiveTab(initialTab);
      setSearchTerm('');
      setShowFilters(false);
      setRamaFilter('todos');
      setStatusFilter('todos');
      setPlayerTipoFilter('todos');
      setSelectedYearsFilter([]);
      setShowOnlyEligible(false);
      setSelectedPlayers(new Set());
      setInlineError(null);
      loadPlayers(group.id);
    }
  }, [isOpen, group?.id, initialTab]);

  const loadPlayers = async (groupId) => {
    try {
      setLoading(true);
      const [{ data: allPlayersData, error: playersError }, { data: groupPlayersData, error: groupPlayersError }] =
        await Promise.all([
          getAllPlayersForAssignment({ groupId, orgId: group?.org_id }),
          getGroupPlayers(groupId)
        ]);

      if (playersError) throw playersError;
      if (groupPlayersError) throw groupPlayersError;

      const mappedGroupPlayers = (groupPlayersData || []).map((assignment) => ({
        ...(assignment.jugadores || {}),
        jugador_grupo: assignment
      }));

      const filteredAllPlayers = (allPlayersData || []).filter((player) => isVisiblePlayer(player));

      const filteredGroupPlayers = (mappedGroupPlayers || []).filter((player) => isVisiblePlayer(player));

      setAllPlayers(filteredAllPlayers);
      setGroupPlayers(filteredGroupPlayers);
      setSelectedPlayers(new Set());
    } catch (error) {
      console.error('Error loading players for modal:', error);
      await alert('Error', 'No se pudieron cargar los jugadores.');
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  const availablePlayerTipos = useMemo(() => {
    const tags = new Set();
    (allPlayers || []).forEach((player) => {
      const sourceTags = player.tipo_tags?.length
        ? player.tipo_tags
        : (player.tipo || '').split(',');
      sourceTags.forEach((tag) => {
        const clean = (tag || '').trim();
        if (clean) tags.add(clean);
      });
    });
    return Array.from(tags).sort((a, b) => a.localeCompare(b, 'es'));
  }, [allPlayers]);

  const assignedIds = useMemo(() => new Set(groupPlayers.map((player) => player.id)), [groupPlayers]);

  const isPlayerEligibleForGroup = useCallback(
    (player) => {
      if (!player) return false;
      if (!isRamaAllowed(group?.rama, player.genero)) return false;
      if (groupCategoryRules.length === 0) {
        return true;
      }
      const birthDate = player.fecha_nacimiento ? parseLocalDate(player.fecha_nacimiento) : null;
      if (!birthDate || Number.isNaN(birthDate.getTime())) return false;
      const birthYear = birthDate.getFullYear();
      return groupCategoryRules.some((rule) => {
        if (rule.deporte === 'tackle') {
          if (rule.supDate && birthDate < rule.supDate) return false;
          if (rule.infDate && birthDate > rule.infDate) return false;
          return true;
        }
        const minYear = rule.anio_fin;
        const maxYear = rule.anio_inicio;
        if (minYear !== null && birthYear < minYear) return false;
        if (maxYear !== null && birthYear > maxYear) return false;
        return true;
      });
    },
    [groupRestrictionType, groupCategoryRules]
  );

  const basePlayers = useMemo(() => {
    if (activeTab === 'remove') {
      return groupPlayers;
    }
    return allPlayers.filter((player) => !assignedIds.has(player.id));
  }, [activeTab, allPlayers, assignedIds, groupPlayers]);

  const filteredPlayers = useMemo(() => {
    return basePlayers.filter((player) => {
      if (!player) return false;
      if (activeTab === 'assign' && !isRamaAllowed(group?.rama, player.genero)) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesSearch =
          player.nombre_completo?.toLowerCase().includes(term) ||
          player.numero?.toString().includes(term) ||
          player.nickname?.toLowerCase().includes(term);
        if (!matchesSearch) return false;
      }

      if (ramaFilter !== 'todos' && player.genero !== ramaFilter) return false;
      if (statusFilter !== 'todos') {
        const normalizedStatus = normalizeStatusValue(player.estatus);
        const allowed = STATUS_ALIASES[statusFilter] || [statusFilter];
        if (!allowed.includes(normalizedStatus)) return false;
      }

      if (playerTipoFilter !== 'todos') {
        const tags = player.tipo_tags?.length
          ? player.tipo_tags
          : (player.tipo || '').split(',');
        const matchesTipo = tags.some((tag) => tag?.toLowerCase().trim() === playerTipoFilter);
        if (!matchesTipo) return false;
      }

      if (selectedYearsFilter.length > 0) {
        const birthYear = player.fecha_nacimiento ? parseLocalDate(player.fecha_nacimiento)?.getFullYear() : null;
        if (!birthYear || !selectedYearsFilter.includes(birthYear)) return false;
      }

      if (showOnlyEligible && categoriaRestringe && activeTab === 'assign') {
        if (!isPlayerEligibleForGroup(player)) return false;
      }

      return true;
    });
  }, [
    basePlayers,
    searchTerm,
    ramaFilter,
    statusFilter,
    playerTipoFilter,
    selectedYearsFilter,
    showOnlyEligible,
    categoriaRestringe,
    activeTab,
    isPlayerEligibleForGroup
  ]);

  const handlePlayerSelection = (playerId) => {
    setInlineError(null);
    setSelectedPlayers((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) {
        next.delete(playerId);
      } else {
        next.add(playerId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedPlayers((prev) => {
      if (prev.size === filteredPlayers.length) {
        return new Set();
      }
      return new Set(filteredPlayers.map((player) => player.id));
    });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (ramaFilter !== 'todos') count++;
    if (statusFilter !== 'todos') count++;
    if (playerTipoFilter !== 'todos') count++;
    if (selectedYearsFilter.length > 0) count++;
    if (showOnlyEligible) count++;
    return count;
  };

  const resetFilters = () => {
    setRamaFilter('todos');
    setStatusFilter('todos');
    setPlayerTipoFilter('todos');
    setSelectedYearsFilter([]);
    setShowOnlyEligible(false);
  };

  const getCategoryViolations = (playersToCheck) => {
    if (!playersToCheck?.length) return { blockers: [], warnings: [] };
    if (groupCategoryRules.length === 0 || !categoriaRestringe) {
      return { blockers: [], warnings: [] };
    }

    return playersToCheck.reduce(
      (acc, player) => {
        if (!isRamaAllowed(group?.rama, player.genero)) {
          acc.blockers.push({ player, reason: 'fuera de la rama permitida' });
          return acc;
        }
        const birthDate = player.fecha_nacimiento ? parseLocalDate(player.fecha_nacimiento) : null;
        if (!birthDate || Number.isNaN(birthDate.getTime())) {
          // Nunca bloquea: es un hueco de captura del club, no una violacion de la
          // regla, y bloquear castigaria al jugador por un dato que nadie escribio.
          acc.warnings.push({ player, reason: 'sin fecha de nacimiento' });
          return acc;
        }

        const birthYear = birthDate.getFullYear();
        const matchesRule = groupCategoryRules.some((rule) => {
          if (rule.deporte === 'tackle') {
            if (rule.supDate && birthDate < rule.supDate) return false;
            if (rule.infDate && birthDate > rule.infDate) return false;
            return true;
          }

          const minYear = rule.anio_fin;
          const maxYear = rule.anio_inicio;
          if (minYear !== null && birthYear < minYear) return false;
          if (maxYear !== null && birthYear > maxYear) return false;
          return true;
        });

        if (!matchesRule) {
          const reason = 'fuera de las categorías permitidas';
          if (groupRestrictionType === 'estricta') {
            acc.blockers.push({ player, reason });
          } else if (groupRestrictionType === 'recomendada') {
            acc.warnings.push({ player, reason });
          }
          // 'sin_restriccion': la categoria no se menciona.
        }
        return acc;
      },
      { blockers: [], warnings: [] }
    );
  };

  const formatCategoryViolationMessage = (violations) =>
    violations
      .map(({ player, reason }) => `${player.nombre_completo} (${reason})`)
      .join(', ');

  const assignSelectedPlayersToGroup = async () => {
    if (!group?.id) return;
    if (selectedPlayers.size === 0) {
      await alert('Error', 'Selecciona al menos un jugador');
      return;
    }

    const playersToAssign = allPlayers.filter((player) => selectedPlayers.has(player.id));
    const { blockers, warnings } = getCategoryViolations(playersToAssign);
    if (blockers.length > 0) {
      // Usar inlineError en lugar de showError porque el Modal compartido
      // tiene un bug donde no cierra al confirmar en este contexto.
      setInlineError(
        `No puedes asignar jugadores fuera de las categorías permitidas: ${formatCategoryViolationMessage(blockers)}`
      );
      return;
    }

    if (warnings.length > 0) {
      const proceed = await confirm(
        'Jugadores fuera de rango recomendado',
        `Estos jugadores no cumplen con las categorias permitidas: ${formatCategoryViolationMessage(
          warnings
        )}. Deseas continuar de todos modos?`,
        'Asignar',
        'Cancelar'
      );
      if (!proceed) return;
    }

    const confirmed = await confirm(
      'Asignar Jugadores',
      `Deseas asignar ${selectedPlayers.size} jugador(es) al grupo "${group.nombre_del_grupo}"?`,
      'Asignar',
      'Cancelar'
    );

    if (!confirmed) return;

    try {
      setSyncing(true);
      const playerIds = Array.from(selectedPlayers);
      let { error } = await assignPlayersToGroup(group.id, playerIds);

      // Candado suave (F2): no se puede estar en un grupo sin inscripcion en la
      // temporada abierta del deporte. Inscribir no exige papeleo (nace
      // 'Pendiente'), asi que en vez de mandar al admin a otra pantalla, se
      // ofrece resolverlo aqui mismo en un clic.
      if (error?.code === 'SIN_INSCRIPCION') {
        const n = error.jugadorIds.length;
        const inscribir = await confirm(
          'Falta inscribir',
          `${n} jugador(es) no están inscritos en "${error.temporada?.nombre}". Se inscribirán en "Pendiente" y se asignarán al grupo. ¿Continuar?`,
          'Inscribir y asignar',
          'Cancelar'
        );
        if (!inscribir) return;
        const { error: iErr } = await inscribirJugadores({
          jugadores: error.jugadorIds.map((jugadorId) => ({ jugadorId, grupoId: group.id })),
          temporada: error.temporada,
        });
        if (iErr) throw iErr;
        ({ error } = await assignPlayersToGroup(group.id, playerIds));
      }

      if (error) throw error;
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error('Error assigning players:', error);
      // Un rechazo de permisos de la base (RLS) decia lo mismo que cualquier otro error:
      // el coach no sabia que era un permiso ni a quien pedirselo.
      const sinPermiso = error?.code === '42501' || /row-level security/i.test(error?.message || '');
      await alert(
        sinPermiso ? 'Sin permiso' : 'Error',
        sinPermiso
          ? 'No tienes permiso para inscribir o asignar estos jugadores. Pídeselo al admin del club.'
          : 'No se pudieron asignar los jugadores'
      );
    } finally {
      setSyncing(false);
    }
  };

  const removeSelectedPlayersFromGroup = async () => {
    if (!group?.id) return;
    if (selectedPlayers.size === 0) {
      await alert('Error', 'Selecciona al menos un jugador');
      return;
    }

    const playersToRemove = groupPlayers.filter((player) => selectedPlayers.has(player.id));
    if (playersToRemove.length === 0) {
      await alert('Error', 'Los jugadores seleccionados no pertenecen al grupo');
      return;
    }

    const confirmed = await confirm(
      'Remover Jugadores',
      `Deseas remover ${playersToRemove.length} jugador(es) del grupo "${group.nombre_del_grupo}"?`,
      'Remover',
      'Cancelar'
    );

    if (!confirmed) return;

    try {
      setSyncing(true);
      const playerIds = playersToRemove.map((player) => player.id);
      const { error } = await removePlayersFromGroup(group.id, playerIds);
      if (error) throw error;
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error('Error removing players:', error);
      await alert('Error', 'No se pudieron remover los jugadores');
    } finally {
      setSyncing(false);
    }
  };

  if (!isOpen || !group) {
    return null;
  }

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/60 p-0 sm:p-4">
      <div className="bg-card w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-4xl sm:rounded-2xl shadow-2xl flex flex-col min-h-0">
        <div className="flex items-start justify-between px-4 py-4 sm:px-6 sm:py-6 border-b border-border">
          <div className="space-y-1">
            <h3 className="text-base sm:text-xl font-semibold text-foreground">Gestión de jugadores</h3>
            <p className="text-sm text-muted-foreground">
              Grupo: <span className="font-medium">{group.nombre_del_grupo}</span>
            </p>
            {groupCategoryRules.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Categorias permitidas:{' '}
                {groupCategoryRules.map((rule) => `${rule.nombre} (${formatCategoryRange(rule)})`).join(', ')}
              </p>
            )}
          </div>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose}>
            <X size={22} />
          </Button>
        </div>

          <div className="px-4 pt-4 sm:px-6">
              <div className="space-y-2 border-b border-border pb-4 mb-4">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'assign', label: 'Asignar jugadores' },
                    { id: 'remove', label: 'Jugadores del grupo' }
                  ].map((tab) => (
                    <Button
                      key={tab.id}
                      variant={activeTab === tab.id ? 'default' : 'outline'}
                      size="sm"
                      className={`h-11 text-sm font-semibold rounded-2xl ${
                        activeTab === tab.id ? 'bg-primary text-primary-foreground hover:bg-primary' : 'bg-card'
                      }`}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setSelectedPlayers(new Set());
                      }}
                    >
                      {tab.label}
                    </Button>
                  ))}
                </div>
                <div className="text-sm text-muted-foreground text-center sm:text-right">
                  {selectedPlayers.size} jugador(es)
                </div>
              </div>
        </div>

        <div className="px-4 sm:px-6 pb-6 flex-1 min-h-0 flex flex-col space-y-6 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {(() => {
                const isDisabled = selectedPlayers.size === 0 || syncing;
                const activeClasses =
                  activeTab === 'assign'
                    ? 'bg-primary text-primary-foreground hover:bg-primary'
                    : 'bg-red-600 text-white hover:bg-red-600';
                const inactiveClasses = 'bg-muted text-muted-foreground border-border cursor-not-allowed';
                const buttonClasses = isDisabled ? inactiveClasses : activeClasses;

                return activeTab === 'assign' ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={assignSelectedPlayersToGroup}
                    disabled={isDisabled}
                    className={`flex items-center space-x-2 ${buttonClasses}`}
                  >
                    {syncing && <RefreshCw size={16} className="animate-spin" />}
                    <UserPlus size={16} />
                    <span>Asignar al grupo</span>
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={removeSelectedPlayersFromGroup}
                    disabled={isDisabled}
                    className={`flex items-center space-x-2 ${buttonClasses}`}
                  >
                    {syncing && <RefreshCw size={16} className="animate-spin" />}
                    <UserMinus size={16} />
                    <span>Remover del grupo</span>
                  </Button>
                );
              })()}
              <span className="text-sm text-muted-foreground">
                {activeTab === 'assign'
                  ? 'Selecciona jugadores para agregarlos al grupo.'
                  : 'Selecciona jugadores para removerlos del grupo.'}
              </span>
            </div>

            {activeTab === 'assign' && (
              <Label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <span>Solo dentro de categoría</span>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => setShowOnlyEligible((prev) => !prev)}
                  className={`relative inline-flex h-6 w-11 p-0 items-center rounded-full transition-colors ${
                    showOnlyEligible ? 'bg-primary' : 'bg-muted'
                  }`}
                  aria-pressed={showOnlyEligible}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-background shadow transition-transform ${
                      showOnlyEligible ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </Button>
              </Label>
            )}
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex-1 relative">
              <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
              <Input
                placeholder="Buscar por nombre, número o nickname..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex-shrink-0">
              <Button
                variant="outline"
                size="default"
                onClick={() => setShowFilters(!showFilters)}
                className="relative"
              >
                <Filter className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Filtros</span>
                {showFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
                {getActiveFiltersCount() > 0 && (
                  <Badge variant="destructive" className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 text-xs flex items-center justify-center">
                    {getActiveFiltersCount()}
                  </Badge>
                )}
              </Button>
            </div>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className="block text-sm font-medium text-muted-foreground mb-2">
                  Filtrar por Rama
                </Label>
                <Select value={ramaFilter} onValueChange={setRamaFilter}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Todas las ramas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todas las ramas</SelectItem>
                    <SelectItem value="Femenil">Femenil</SelectItem>
                    <SelectItem value="Varonil">Varonil</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="block text-sm font-medium text-muted-foreground mb-2">
                  Filtrar por Año de Nacimiento
                </Label>
                <YearMultiSelect
                  selectedYears={selectedYearsFilter}
                  onYearsChange={setSelectedYearsFilter}
                />
              </div>

              <div>
                <Label className="block text-sm font-medium text-muted-foreground mb-2">
                  Filtrar por Estado
                </Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Todos los estados" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los estados</SelectItem>
                    <SelectItem value="activo">Activos</SelectItem>
                    <SelectItem value="inactivo">Inactivos</SelectItem>
                    <SelectItem value="suspendido">Suspendidos</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="block text-sm font-medium text-muted-foreground mb-2">
                  Filtrar por Tipo
                </Label>
                <Select value={playerTipoFilter} onValueChange={setPlayerTipoFilter}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Todos los tipos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los tipos</SelectItem>
                    {availablePlayerTipos.map((tipo) => (
                      <SelectItem key={tipo} value={tipo.toLowerCase()}>
                        {tipo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-full flex justify-end">
                <Button variant="secondary" size="sm" onClick={resetFilters}>
                  Limpiar Filtros
                </Button>
              </div>
            </div>
          )}

          <div className="flex-1 min-h-0">
            {inlineError && (
              <div className="mb-3 rounded-lg border border-red-300 bg-red-50 p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                <div className="flex-1 text-sm text-red-800">{inlineError}</div>
                <button
                  type="button"
                  onClick={() => setInlineError(null)}
                  className="text-red-600 hover:bg-red-100 rounded p-0.5"
                  aria-label="Cerrar"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <div className="bg-card rounded-lg shadow-sm border overflow-hidden h-full flex flex-col">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Users className="text-foreground" size={20} />
                <span className="text-foreground font-semibold">
                  {activeTab === 'assign' ? 'Jugadores disponibles' : 'Jugadores del grupo'}
                </span>
                <span className="bg-primary text-primary-foreground text-sm font-medium px-2 py-1 rounded-full">
                  {filteredPlayers.length}
                </span>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleSelectAll}
                className="flex items-center space-x-2"
              >
                <Check size={16} />
                <span>
                  {selectedPlayers.size === filteredPlayers.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
                </span>
              </Button>
            </div>

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-muted-foreground font-medium">Cargando jugadores...</p>
                  </div>
                </div>
              ) : filteredPlayers.length === 0 ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center px-6">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="text-muted-foreground" size={32} />
                    </div>
                    <h3 className="text-foreground font-semibold text-lg mb-2">No hay jugadores disponibles</h3>
                    <p className="text-muted-foreground text-sm max-w-sm">
                      Ajusta los filtros o cambia de pestana.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 min-h-0 overflow-y-auto max-h-[45vh] sm:max-h-[50vh]">
                  {filteredPlayers.map((player) => (
                    <div
                      key={player.id}
                      className={`px-4 py-3 border-b border-border hover:bg-muted/50 cursor-pointer transition-colors ${
                        selectedPlayers.has(player.id) ? 'bg-blue-50 border-blue-200' : ''
                      }`}
                      onClick={() => handlePlayerSelection(player.id)}
                    >
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedPlayers.has(player.id)}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayerSelection(player.id);
                          }}
                          readOnly
                          className="w-4 h-4 text-blue-600 border-input rounded focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-foreground font-medium truncate">
                                #{player.numero} {player.nombre_completo}
                              </p>
                              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                                <span className="capitalize">{player.genero}</span>
                                {(() => {
                                  if (!player.fecha_nacimiento) return null;
                                  const birthDate = parseLocalDate(player.fecha_nacimiento);
                                  if (Number.isNaN(birthDate.getTime())) return null;
                                  const birthYear = birthDate.getFullYear();
                                  const age = currentYear - birthYear;
                                  return (
                                    <span>
                                      {birthYear} ({age} anos)
                                    </span>
                                  );
                                })()}
                                {player.nickname && <span>"{player.nickname}"</span>}
                                <span
                                  className={`px-2 py-1 rounded text-xs font-medium ${
                                    player.estatus === 'activo'
                                      ? 'bg-green-100 text-green-800'
                                      : player.estatus === 'inactivo'
                                        ? 'bg-muted text-foreground'
                                        : 'bg-red-100 text-red-800'
                                  }`}
                                >
                                  {player.estatus}
                                </span>
                              </div>
                            </div>
                            <div className="text-right text-sm">
                              <p className="text-muted-foreground font-medium">
                                {getPlayerGroupNames(player)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    <ConfirmDialog />
    </>
  );
};

export default GroupRosterModal;
















